// src/salesAssessment/SalesAssessmentComparisonResults.js
import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  LinearProgress,
  Alert,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Error as ErrorIcon,
  TrendingUp as TrendingUpIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import api from '../config/api';
import useStore from '../config/store';
import { useTranslation } from 'react-i18next';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  marginBottom: theme.spacing(3),
}));

// Colores para cada persona en la tabla (hasta 10)
const PERSON_COLORS = [
  '#1565c0', '#2e7d32', '#c62828', '#e65100',
  '#6a1b9a', '#00838f', '#f57f17', '#37474f',
  '#880e4f', '#1b5e20',
];

// Colores relativos por fila: más alto = verde, medios = naranja, más bajo = rojo
const getRelativeStyle = (score, rowScores) => {
  if (rowScores.length <= 1) return { bgcolor: 'transparent', color: 'inherit' };
  const max = Math.max(...rowScores);
  const min = Math.min(...rowScores);
  if (max === min) return { bgcolor: 'transparent', color: 'inherit' };
  if (score === max) return { bgcolor: '#e8f5e9', color: '#2e7d32' };
  if (score === min) return { bgcolor: '#ffebee', color: '#c62828' };
  return { bgcolor: '#fff3e0', color: '#e65100' };
};

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export default function SalesAssessmentComparisonResults({ comparisonId, onBack }) {
  const { authToken } = useStore.getState();
  const { t } = useTranslation();

  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    fetchComparison();
    startPolling();
    return () => stopPolling();
  }, [comparisonId]);

  const startPolling = () => {
    intervalRef.current = setInterval(fetchComparison, 3000);
  };

  const stopPolling = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const fetchComparison = async () => {
    try {
      const { data } = await api.get(
        `/sales-assessment-comparisons/${comparisonId}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (data.response && data.response.status_response === 'OK') {
        const comp = data.response.data;
        setComparison(comp);
        if (comp.status === 'completado' || comp.status === 'error') {
          stopPolling();
          setLoading(false);
        }
      } else {
        setError(t('salesAssessment.comparison.fetchError'));
        stopPolling();
        setLoading(false);
      }
    } catch (err) {
      console.error('Error al obtener comparación:', err);
      setError(err.message);
      stopPolling();
      setLoading(false);
    }
  };

  const handleRetry = async () => {
    try {
      setError(null);
      await api.post(
        `/sales-assessment-comparisons/${comparisonId}/retry`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      setLoading(true);
      startPolling();
    } catch (err) {
      setError(err.message);
    }
  };

  // ─── ESTADO: procesando ────────────────────────────────────────────────────
  if (!comparison || comparison.status === 'pendiente' || comparison.status === 'procesando') {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 3 }}>
          {t('back')}
        </Button>
        <StyledPaper>
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={60} sx={{ mb: 3 }} />
            <Typography variant="h5" gutterBottom>
              {t('salesAssessment.comparison.processing')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('salesAssessment.comparison.processingDescription')}
            </Typography>
            <LinearProgress sx={{ maxWidth: 400, mx: 'auto' }} />
            {comparison?.logs?.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="caption" color="text.secondary">
                  {comparison.logs[comparison.logs.length - 1].message}
                </Typography>
              </Box>
            )}
          </Box>
        </StyledPaper>
      </Box>
    );
  }

  // ─── ESTADO: error ────────────────────────────────────────────────────────
  if (comparison.status === 'error') {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 3 }}>
          {t('back')}
        </Button>
        <Alert
          severity="error"
          icon={<ErrorIcon />}
          action={
            <Button color="inherit" size="small" onClick={handleRetry}>
              <RefreshIcon sx={{ mr: 0.5 }} />
              {t('retry')}
            </Button>
          }
        >
          <Typography variant="subtitle2">
            {t('salesAssessment.comparison.processingError')}
          </Typography>
          <Typography variant="caption">
            {comparison.error || t('salesAssessment.comparison.unknownError')}
          </Typography>
        </Alert>
      </Box>
    );
  }

  // ─── ESTADO: completado ───────────────────────────────────────────────────
  const { tests = [], result } = comparison;
  const recommendations = result?.recommendations || [];

  // Obtener lista de competencias del primer test
  const competencyNames = tests.length > 0 ? Object.keys(tests[0].competencies || {}) : [];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack}>
          {t('back')}
        </Button>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={700}>
            {t('salesAssessment.comparison.resultsTitle')}
          </Typography>
          {comparison.position_name && (
            <Typography variant="body2" color="text.secondary">
              {comparison.position_name}
            </Typography>
          )}
        </Box>
        <Chip
          label={`${tests.length} ${t('salesAssessment.comparison.persons')}`}
          icon={<PersonIcon />}
          color="primary"
          variant="outlined"
        />
      </Box>

      {/* Tabs: Tabla comparativa | Recomendaciones */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>
        <Tabs value={tabValue} onChange={(e, val) => setTabValue(val)}>
          <Tab label={t('salesAssessment.comparison.tabCompetencies')} />
          <Tab label={t('salesAssessment.comparison.tabDevelopment')} />
        </Tabs>
      </Box>

      {/* ── Tab 0: Tabla de 18 competencias ── */}
      <TabPanel value={tabValue} index={0}>
        <StyledPaper sx={{ p: 0, overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      bgcolor: '#f5f5f5',
                      minWidth: 200,
                      position: 'sticky',
                      left: 0,
                      zIndex: 3,
                    }}
                  >
                    {t('salesAssessment.comparison.competency')}
                  </TableCell>
                  {tests.map((test, idx) => (
                    <TableCell
                      key={test.id}
                      align="center"
                      sx={{
                        fontWeight: 700,
                        bgcolor: '#f5f5f5',
                        minWidth: 130,
                        borderLeft: `3px solid ${PERSON_COLORS[idx % PERSON_COLORS.length]}`,
                      }}
                    >
                      <Typography variant="caption" fontWeight={700} display="block">
                        {test.applicantName}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {competencyNames.map((competency, rowIdx) => (
                  <TableRow
                    key={competency}
                    sx={{ bgcolor: rowIdx % 2 === 0 ? 'white' : '#fafafa' }}
                  >
                    <TableCell
                      sx={{
                        fontWeight: 500,
                        position: 'sticky',
                        left: 0,
                        bgcolor: rowIdx % 2 === 0 ? 'white' : '#fafafa',
                        zIndex: 1,
                      }}
                    >
                      {competency}
                    </TableCell>
                    {tests.map((test, idx) => {
                      const score = test.competencies?.[competency] ?? 0;
                      const rowScores = tests.map(t => t.competencies?.[competency] ?? 0);
                      const relStyle = getRelativeStyle(score, rowScores);
                      return (
                        <TableCell
                          key={test.id}
                          align="center"
                          sx={{
                            borderLeft: `3px solid ${PERSON_COLORS[idx % PERSON_COLORS.length]}`,
                          }}
                        >
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 44,
                              height: 28,
                              borderRadius: 1,
                              bgcolor: relStyle.bgcolor,
                              color: relStyle.color,
                              fontWeight: 700,
                              fontSize: '0.875rem',
                            }}
                          >
                            {score.toFixed(1)}
                          </Box>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </StyledPaper>

        {/* Leyenda */}
        <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
          {[
            { label: t('salesAssessment.comparison.legendHighest'), color: '#2e7d32', bg: '#e8f5e9' },
            { label: t('salesAssessment.comparison.legendMiddle'),  color: '#e65100', bg: '#fff3e0' },
            { label: t('salesAssessment.comparison.legendLowest'),  color: '#c62828', bg: '#ffebee' },
          ].map(({ label, color, bg }) => (
            <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{ width: 24, height: 16, borderRadius: 0.5, bgcolor: bg, border: `1px solid ${color}` }}
              />
              <Typography variant="caption">{label}</Typography>
            </Box>
          ))}
        </Box>
      </TabPanel>

      {/* ── Tab 1: Plan de desarrollo por persona ── */}
      <TabPanel value={tabValue} index={1}>
        <Alert severity="info" icon={<TrendingUpIcon />} sx={{ mb: 3 }}>
          {t('salesAssessment.comparison.developmentSubtitle')}
        </Alert>
        {recommendations.length === 0 ? (
          <Alert severity="info">
            {t('salesAssessment.comparison.noRecommendations')}
          </Alert>
        ) : (
          <Box>
            {recommendations.map((rec, idx) => {
              const personColor = PERSON_COLORS[idx % PERSON_COLORS.length];
              return (
                <StyledPaper key={rec.applicantEmail || idx}>
                  {/* ── Cabecera de persona ── */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        bgcolor: personColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '1.25rem',
                        flexShrink: 0,
                      }}
                    >
                      {rec.applicantName?.charAt(0)?.toUpperCase()}
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h5" fontWeight={700}>
                        {rec.applicantName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {rec.applicantEmail}
                      </Typography>
                    </Box>
                    <Chip
                      label={`${(rec.competencies || []).length} ${t('salesAssessment.comparison.competenciesToDevelop')}`}
                      color="warning"
                      variant="outlined"
                    />
                  </Box>

                  <Divider sx={{ mb: 3, borderColor: personColor, borderBottomWidth: 2 }} />

                  {/* ── Competencias a desarrollar ── */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {(rec.competencies || []).map((comp, cIdx) => {
                      // Colores alternados por competencia, igual que DISC usa secciones
                      const sectionColors = [
                        { bg: '#f5f5f5', border: '#bdbdbd' },
                        { bg: '#e3f2fd', border: '#90caf9' },
                        { bg: '#fff3e0', border: '#ffcc80' },
                      ];
                      const sc = sectionColors[cIdx % sectionColors.length];

                      return (
                        <Box
                          key={comp.nombre || cIdx}
                          sx={{
                            border: `1px solid ${sc.border}`,
                            borderRadius: 2,
                            overflow: 'hidden',
                          }}
                        >
                          {/* Título de competencia */}
                          <Box
                            sx={{
                              bgcolor: sc.bg,
                              px: 2,
                              py: 1.5,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.5,
                            }}
                          >
                            <Chip
                              label={cIdx + 1}
                              size="small"
                              sx={{
                                bgcolor: personColor,
                                color: 'white',
                                fontWeight: 700,
                                minWidth: 28,
                              }}
                            />
                            <Typography variant="subtitle1" fontWeight={700}>
                              {comp.nombre}
                            </Typography>
                            {(() => {
                              const testData = tests.find(t => t.applicantEmail === rec.applicantEmail);
                              const score = testData?.competencies?.[comp.nombre];
                              return score !== undefined ? (
                                <Chip
                                  label={score.toFixed(1)}
                                  size="small"
                                  sx={{ bgcolor: '#ffebee', color: '#c62828', fontWeight: 700, ml: 0.5 }}
                                />
                              ) : null;
                            })()}
                          </Box>

                          <Box sx={{ px: 2, py: 2 }}>
                            {/* Objetivo de mejora */}
                            <Box
                              sx={{
                                bgcolor: '#fafafa',
                                border: '1px solid #e0e0e0',
                                borderRadius: 1,
                                p: 1.5,
                                mb: 2,
                              }}
                            >
                              <Typography
                                variant="caption"
                                fontWeight={700}
                                color="text.secondary"
                                display="block"
                                sx={{ mb: 0.5 }}
                              >
                                {t('salesAssessment.comparison.objective')}
                              </Typography>
                              <Typography variant="body2">
                                {comp.objetivoDeMejora}
                              </Typography>
                            </Box>

                            {/* Acciones */}
                            <Typography
                              variant="caption"
                              fontWeight={700}
                              color="text.secondary"
                              display="block"
                              sx={{ mb: 1 }}
                            >
                              {t('salesAssessment.comparison.actions')}
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              {(comp.acciones || []).map((accion, aIdx) => (
                                <Box
                                  key={aIdx}
                                  sx={{
                                    display: 'flex',
                                    gap: 1.5,
                                    alignItems: 'flex-start',
                                  }}
                                >
                                  <Box
                                    sx={{
                                      minWidth: 24,
                                      height: 24,
                                      borderRadius: '50%',
                                      bgcolor: '#e3f2fd',
                                      color: '#1565c0',
                                      fontWeight: 700,
                                      fontSize: '0.75rem',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      flexShrink: 0,
                                      mt: 0.25,
                                    }}
                                  >
                                    {aIdx + 1}
                                  </Box>
                                  <Typography variant="body2" sx={{ pt: 0.25 }}>
                                    {accion}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </StyledPaper>
              );
            })}
          </Box>
        )}
      </TabPanel>
    </Box>
  );
}